import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  model,
  viewChildren,
} from '@angular/core';

let uiCodeInputUid = 0;

/**
 * Átomo: un solo control OTP de N cajas numéricas.
 * Sin HTTP ni stores. El valor es la cadena de dígitos concatenada.
 */
@Component({
  selector: 'ui-code-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './code-input.component.html',
  styleUrl: './code-input.component.scss',
  host: {
    class: 'ui-code-input-host',
  },
})
export class UiCodeInputComponent {
  readonly label = input('');
  readonly value = model('');
  readonly length = input(6);

  readonly required = input(false);
  readonly showOptionalHint = input(true);
  readonly requiredHint = input('Obligatorio');
  readonly optionalHint = input('Opcional');
  readonly showLabel = input(true);

  readonly disabled = input(false);
  readonly invalid = input(false);
  readonly message = input<string | undefined>(undefined);
  readonly showMessage = input(true);

  readonly fieldId = input<string | undefined>(undefined);

  protected readonly generatedId = `ui-code-input-${++uiCodeInputUid}`;

  protected readonly groupId = computed(
    () => this.fieldId() ?? this.generatedId,
  );

  protected readonly messageId = computed(() => `${this.groupId()}-message`);

  protected readonly cells = computed(() => {
    const len = this.normalizedLength();
    const digits = this.normalizedValue();
    return Array.from({ length: len }, (_, index) => digits[index] ?? '');
  });

  protected readonly showHeader = computed(
    () => this.showLabel() && !!this.label(),
  );

  protected readonly showRequirement = computed(
    () => this.required() || this.showOptionalHint(),
  );

  protected readonly requirementText = computed(() =>
    this.required() ? this.requiredHint() : this.optionalHint(),
  );

  protected readonly visibleMessage = computed(() => {
    if (!this.showMessage()) return '';
    return this.message() ?? '';
  });

  private readonly cellRefs =
    viewChildren<ElementRef<HTMLInputElement>>('cell');

  protected onCellInput(index: number, event: Event): void {
    if (this.disabled()) return;
    const el = event.target as HTMLInputElement;
    const incoming = el.value.replace(/\D/g, '');

    if (incoming.length > 1) {
      this.applyDigits(incoming, index);
      return;
    }

    const digit = incoming.slice(-1);
    const next = this.cellValues();
    next[index] = digit;
    this.commit(next);
    el.value = digit;

    if (digit) {
      this.focusCell(index + 1);
    }
  }

  protected onCellKeydown(index: number, event: KeyboardEvent): void {
    if (this.disabled()) return;

    if (event.key === 'Backspace') {
      event.preventDefault();
      const next = this.cellValues();
      if (next[index]) {
        next[index] = '';
        this.commit(next);
        return;
      }
      if (index > 0) {
        next[index - 1] = '';
        this.commit(next);
        this.focusCell(index - 1);
      }
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.focusCell(index - 1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.focusCell(index + 1);
    }
  }

  protected onCellPaste(index: number, event: ClipboardEvent): void {
    if (this.disabled()) return;
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    this.applyDigits(pasted, index);
  }

  protected onCellFocus(event: FocusEvent): void {
    const el = event.target as HTMLInputElement;
    el.select();
  }

  private applyDigits(raw: string, startIndex: number): void {
    const incoming = raw.replace(/\D/g, '');
    if (!incoming) return;

    const next = this.cellValues();
    const len = this.normalizedLength();
    let cursor = startIndex;

    for (const digit of incoming) {
      if (cursor >= len) break;
      next[cursor] = digit;
      cursor += 1;
    }

    this.commit(next);
    this.focusCell(Math.min(cursor, len - 1));
  }

  private cellValues(): string[] {
    return [...this.cells()];
  }

  private commit(digits: string[]): void {
    this.value.set(
      digits.join('').replace(/\D/g, '').slice(0, this.normalizedLength()),
    );
    this.syncCells();
  }

  private syncCells(): void {
    const digits = this.cells();
    this.cellRefs().forEach((ref, index) => {
      ref.nativeElement.value = digits[index] ?? '';
    });
  }

  private normalizedValue(): string {
    return this.value().replace(/\D/g, '').slice(0, this.normalizedLength());
  }

  private normalizedLength(): number {
    const len = this.length();
    return Number.isFinite(len) && len > 0 ? Math.floor(len) : 6;
  }

  private focusCell(index: number): void {
    const cells = this.cellRefs();
    if (cells.length === 0) return;
    const target = cells[Math.min(Math.max(index, 0), cells.length - 1)];
    target?.nativeElement.focus();
    target?.nativeElement.select();
  }
}
